import React from "react";
import { Box, ListSubheader, Typography, TextField, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import { useRouter } from 'next/router';
import * as gtag from "../../../../lib/google/gtag";
import * as send from '../../../../lib/analytics/send';
import { searchTicker, searchFund } from "../../../utils/helpers";
import { createListboxComponent, StyledPopper, LISTBOX_PADDING_VALUE } from '../SearchCommon/VirtualizedListbox';

// ─── Module-level cache ─────────────────────────────────────────────────────
// Persists across mount/unmount cycles — data fetched once per page session
let _cache = { stocks: null, funds: null, merged: null };
let _prefetchPromise = null;

function tagStocks(stocks) {
  return stocks.map((s) => ({ ...s, _type: 'stock' }));
}

function tagFunds(funds) {
  return funds.map((f) => ({ ...f, _type: 'fund' }));
}

async function fetchAllData() {
  const [stocks, funds] = await Promise.all([
    searchTicker({ url: '/api/stock-search', searchString: '' }).then((r) => r || []),
    searchFund({ url: '/api/fund-search', searchString: '' }).then((r) => r || []),
  ]);
  const taggedStocks = tagStocks(stocks);
  const taggedFunds = tagFunds(funds);
  _cache.stocks = taggedStocks;
  _cache.funds = taggedFunds;
  _cache.merged = [...taggedStocks, ...taggedFunds];
  return _cache.merged;
}

async function fetchFilteredData(query) {
  const [stocks, funds] = await Promise.all([
    searchTicker({ url: '/api/stock-search', searchString: query }).then((r) => r || []),
    searchFund({ url: '/api/fund-search', searchString: query }).then((r) => r || []),
  ]);
  return [...tagStocks(stocks), ...tagFunds(funds)];
}

function scheduleIdlePrefetch() {
  if (_cache.merged || _prefetchPromise) return;
  const start = () => { _prefetchPromise = fetchAllData(); };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 3000 });
  } else if (typeof window !== 'undefined') {
    setTimeout(start, 1500);
  }
}

function filterLocally(allOptions, trimmed) {
  const matchedStocks = allOptions
    .filter((item) => item._type === 'stock' && (
      item.n1?.toLowerCase().startsWith(trimmed) || item.t?.toLowerCase().startsWith(trimmed)
    ))
    .sort((a, b) => a.t.length - b.t.length);

  const matchedFunds = allOptions
    .filter((item) => item._type === 'fund' && item.n?.toLowerCase().includes(trimmed));

  return [...matchedStocks, ...matchedFunds];
}

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING_VALUE,
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader
        key={dataSet.key}
        component="div"
        style={inlineStyle}
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'grey.500',
          py: 0,
          lineHeight: '48px',
          height: '48px !important',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        {dataSet.group}
      </ListSubheader>
    );
  }

  const option = dataSet[1];
  if (!option) return null;

  const isStock = option._type === 'stock';

  if (isStock) {
    if (!option.t) return null;
    return (
      <Box
        component="li"
        {...dataSet[0]}
        style={inlineStyle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: '16px !important',
          cursor: 'pointer',
          transition: 'background-color 150ms ease',
          '&:hover, &.Mui-focused': {
            bgcolor: 'grey.50',
          },
          '&[aria-selected="true"]': {
            bgcolor: '#eff6ff !important',
            borderLeft: '2px solid',
            borderColor: 'secondary.main',
            pl: '14px !important',
          },
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            color: 'text.primary',
            fontWeight: 400,
            fontSize: '0.875rem',
            minWidth: 0,
          }}
        >
          {option.n1}
        </Typography>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: '0.75rem',
            fontWeight: 600,
            fontFamily: '"SF Mono", "Roboto Mono", "Consolas", monospace',
            color: 'primary.main',
            bgcolor: 'grey.100',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            letterSpacing: '0.03em',
            lineHeight: 1.6,
          }}
        >
          {option.t}
        </Box>
      </Box>
    );
  }

  // Fund item
  if (!option.n) return null;
  return (
    <Box
      component="li"
      {...dataSet[0]}
      style={inlineStyle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: '16px !important',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        '&:hover, &.Mui-focused': {
          bgcolor: 'grey.50',
        },
        '&[aria-selected="true"]': {
          bgcolor: '#eff6ff !important',
          borderLeft: '2px solid',
          borderColor: 'secondary.main',
          pl: '14px !important',
        },
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{
          flex: 1,
          color: 'text.primary',
          fontWeight: 400,
          fontSize: '0.875rem',
          minWidth: 0,
        }}
      >
        {option.n}
      </Typography>
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          fontSize: '0.65rem',
          fontWeight: 600,
          color: 'grey.500',
          bgcolor: 'grey.100',
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          letterSpacing: '0.04em',
          lineHeight: 1.6,
          textTransform: 'uppercase',
        }}
      >
        Fund
      </Box>
    </Box>
  );
}

const ListboxComponent = createListboxComponent(renderRow);

const SearchUnified = ({ setShowSearch, removebackdrop, placeholder, dataEventFrom, variant = 'dark' }) => {
  const isLight = variant === 'light';
  const [value, setValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState(() => _cache.merged || []);
  const [serverResults, setServerResults] = React.useState(null);
  const [prefetchReady, setPrefetchReady] = React.useState(!!_cache.merged);
  const [serverLoading, setServerLoading] = React.useState(false);
  const router = useRouter();
  const debounceRef = React.useRef(null);
  const inputRef = React.useRef('');

  const loading = open && !prefetchReady && options.length === 0 && !serverLoading;

  // Idle prefetch on mount
  React.useEffect(() => {
    if (_cache.merged) {
      setOptions(_cache.merged);
      setPrefetchReady(true);
      return;
    }
    scheduleIdlePrefetch();
    // Poll for prefetch completion
    const interval = setInterval(() => {
      if (_cache.merged) {
        setOptions(_cache.merged);
        setPrefetchReady(true);
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // When dropdown opens: ensure data is loading
  React.useEffect(() => {
    if (!open) return;
    if (_cache.merged) {
      setOptions(_cache.merged);
      setPrefetchReady(true);
      return;
    }
    // Prefetch not done yet — kick it off immediately if not started
    if (!_prefetchPromise) {
      _prefetchPromise = fetchAllData();
    }
    let active = true;
    _prefetchPromise.then(() => {
      if (active && _cache.merged) {
        setOptions(_cache.merged);
        setPrefetchReady(true);
      }
    });
    return () => { active = false; };
  }, [open]);

  // Debounced server-side search fallback (only when prefetch hasn't finished)
  const handleInputChange = React.useCallback((event, newInputValue) => {
    setValue(newInputValue);
    inputRef.current = newInputValue;

    // If full cache is available, local filtering handles it — no server call needed
    if (prefetchReady) {
      setServerResults(null);
      return;
    }

    // Prefetch not ready — debounce a server-side filtered search
    const trimmed = newInputValue.trim();
    if (trimmed === '') {
      setServerResults(null);
      setServerLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    setServerLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchFilteredData(trimmed);
        // Only apply if input hasn't changed and prefetch still not ready
        if (inputRef.current.trim() === trimmed && !_cache.merged) {
          setServerResults(results);
        }
      } catch {
        // Ignore — prefetch will eventually provide data
      } finally {
        setServerLoading(false);
      }
    }, 200);
  }, [prefetchReady]);

  // Clean up debounce on unmount
  React.useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleSelect = (event, newValue) => {
    setValue(newValue);
    if (!router || !newValue) return;

    if (newValue._type === 'stock' && newValue.t) {
      router.push(`/stock/${newValue.t}`);
      send.uevent(event, `stock-search-${dataEventFrom} ${newValue.t}`);
      gtag.event({ action: "ticker_search", search_term: newValue.t });
    } else if (newValue._type === 'fund' && newValue.f) {
      router.push(`/funds/${newValue.f}`);
      send.uevent(event, `fund-search-${dataEventFrom} ${newValue.f}`);
      gtag.event({ action: "fund_search", search_term: newValue.f });
    }
  };

  const textColor = isLight ? 'text.primary' : '#fff';
  const placeholderColor = isLight ? 'grey.500' : 'rgba(255,255,255,0.55)';
  const iconColor = isLight ? 'grey.400' : 'rgba(255,255,255,0.7)';

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        type="search"
        data-event-name="Search"
        data-event-category={dataEventFrom}
        data-event-label="Unified Search"
        autoHighlight
        id="search-unified"
        disableListWrap
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-endAdornment svg': { color: iconColor },
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => { setOpen(false); setShowSearch(false); }}
        onChange={handleSelect}
        onInputChange={handleInputChange}
        isOptionEqualToValue={(option, val) => {
          if (option._type === 'stock') return option.t === val?.t;
          return option.f === val?.f;
        }}
        groupBy={(option) => option._type === 'stock' ? 'Stocks' : 'Funds'}
        options={options}
        loading={loading || serverLoading}
        filterOptions={(opts, { inputValue }) => {
          const trimmed = inputValue.trim().toLowerCase();
          if (trimmed === '') return [];

          // If full cache loaded, filter locally (instant)
          if (prefetchReady) {
            return filterLocally(opts, trimmed);
          }

          // Prefetch not ready — use server-filtered results if available
          if (serverResults) return serverResults;

          // Nothing available yet
          return [];
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          if (option.inputValue) return option.inputValue;
          if (option._type === 'stock') return option.n1 || '';
          if (option._type === 'fund') return option.n || '';
          return '';
        }}
        selectOnFocus
        clearOnBlur
        clearOnEscape
        handleHomeEndKeys
        freeSolo
        noOptionsText={
          <Typography variant="body2" sx={{ color: 'grey.500', py: 0.5 }}>
            No matching stocks or funds found
          </Typography>
        }
        renderOption={(props, option) => [props, option]}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Search stocks or funds..."}
            size="small"
            variant="filled"
            hiddenLabel
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Search stocks or funds',
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5, mr: 0.5 }}>
                    <SearchIcon sx={{ color: iconColor, fontSize: '1.25rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} sx={{ color: iconColor, mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
            sx={{
              width: '100%',
              '& .MuiFilledInput-root': {
                py: '6px !important',
                background: 'transparent',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: textColor,
                '&:hover': { background: 'transparent' },
                '&.Mui-focused': { background: 'transparent' },
                '&::before, &::after': { display: 'none' },
              },
              '& .MuiFilledInput-input': {
                color: textColor,
                '&::placeholder': { color: placeholderColor, opacity: 1 },
              },
            }}
          />
        )}
        renderGroup={(params) => params}
      />
    </Box>
  );
};

export default SearchUnified;
